import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Filter, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { materials, categories, countries } from '../mock/mockData';
import MaterialQuoteModal from './modals/MaterialQuoteModal';
import { useTranslation } from '../hooks/useTranslation';

const Materials = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [showInStock, setShowInStock] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const filteredMaterials = useMemo(() => {
    return materials.filter(material => {
      const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           material.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || 
                             material.category.toLowerCase() === selectedCategory.toLowerCase();
      const matchesCountry = selectedCountry === 'all' || 
                            material.country.toLowerCase() === selectedCountry.toLowerCase();
      const matchesStock = !showInStock || material.inStock;
      
      return matchesSearch && matchesCategory && matchesCountry && matchesStock;
    });
  }, [searchTerm, selectedCategory, selectedCountry, showInStock]);

  return (
    <section id="materials" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
            {t('materials.title')}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('materials.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 rounded-xl p-6 mb-12">
          <div className="grid md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('materials.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
            </div>

            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t('materials.category')} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.name} ({category.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Country Filter */}
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger>
                <SelectValue placeholder={t('materials.country')} />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.value} value={country.value}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Stock Filter */}
            <Button
              variant={showInStock ? "default" : "outline"}
              onClick={() => setShowInStock(!showInStock)}
              className="justify-start"
            >
              <Filter size={16} className="mr-2" />
              {t('materials.inStockOnly')}
            </Button>
          </div>
          
          <div className="text-sm text-gray-600">
            {t('materials.showing')} {filteredMaterials.length} {t('materials.of')} {materials.length} {t('materials.materials')}
          </div>
        </div>

        {/* Materials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredMaterials.map((material) => (
            <Card key={material.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="relative">
                <img
                  src={material.image}
                  alt={material.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 right-4">
                  {material.inStock ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle size={12} className="mr-1" />
                      {t('materials.inStock')}
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      <XCircle size={12} className="mr-1" />
                      {t('materials.outOfStock')}
                    </Badge>
                  )}
                </div>
              </div>
              
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">
                      {material.name}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-2">
                      <MapPin size={14} className="mr-1" />
                      <span className="text-sm">{material.country}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-semibold text-gray-900">
                      {material.price}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {material.description}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline" className="text-xs">
                    {material.category}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {material.certification}
                  </Badge>
                </div>
                
                <Button 
                  onClick={() => {
                    setSelectedMaterial(material);
                    setIsQuoteModalOpen(true);
                  }}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  disabled={!material.inStock}
                >
                  {material.inStock ? t('materials.requestQuote') : t('materials.notifyWhenAvailable')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredMaterials.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Search size={24} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">{t('materials.noMaterialsFound')}</h3>
            <p className="text-gray-600">{t('materials.tryAdjusting')}</p>
          </div>
        )}
        
        <MaterialQuoteModal 
          isOpen={isQuoteModalOpen}
          onClose={() => {
            setIsQuoteModalOpen(false);
            setSelectedMaterial(null);
          }}
          material={selectedMaterial}
        />
      </div>
    </section>
  );
};

export default Materials;